import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateBR } from '../lib/format';
import { formatPhoneBR } from '../components/PhoneInput';
import { formatDocBR } from '../components/DocumentInput';

export default function VehicleTerm() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}/summary`);
        setV(data.vehicle || data);
      } catch (e) {
        setV(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (v) {
      const brand = (v.brand || '').trim();
      const model = (v.model || '').trim();
      const plate = (v.plate || '').trim();
      document.title = `TERMO - ${brand} ${model} - ${plate}`.replace(/\s+/g, ' ').trim();
    } else {
      document.title = 'Termo de Entrega';
    }
  }, [v]);

  if (loading) return <div style={{ padding: 40, fontFamily: 'serif' }}>Carregando termo…</div>;
  if (!v) return <div style={{ padding: 40, fontFamily: 'serif' }}>Veículo não encontrado.</div>;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 22mm 22mm 22mm 22mm; }
        body { background: #e9ecef; margin: 0; }
        :root {
          --ink: #0f172a;
          --rule: #1f2937;
          --muted: #4b5563;
          --soft: #e5e7eb;
        }
        .doc-wrap {
          max-width: 800px;
          margin: 28px auto;
          background: white;
          color: var(--ink);
          font-family: 'Cambria', 'Georgia', 'Times New Roman', serif;
          font-size: 11pt;
          line-height: 1.55;
          padding: 56px 64px 72px;
          box-shadow: 0 4px 24px rgba(15,23,42,0.10);
          position: relative;
        }
        .doc-print-toolbar {
          position: fixed; top: 12px; right: 12px;
          display: flex; gap: 8px; z-index: 50;
        }
        .doc-print-toolbar button {
          background: var(--ink); color: white; border: none;
          font: 600 12px/1 'Inter', system-ui, sans-serif;
          padding: 10px 14px; border-radius: 6px; cursor: pointer;
          letter-spacing: 0.4px; text-transform: uppercase;
        }
        .doc-print-toolbar button:hover { background: #1f2937; }
        .doc-print-toolbar button.ghost {
          background: white; color: var(--ink); border: 1px solid var(--soft);
        }
        .doc-header {
          display: flex; justify-content: space-between; align-items: flex-end;
          border-bottom: 2px solid var(--rule);
          padding-bottom: 14px; margin-bottom: 26px;
        }
        .doc-header .brand {
          font: 700 18pt/1 'Cambria', 'Georgia', serif;
          letter-spacing: 1.6px;
        }
        .doc-header .brand small {
          display: block; font: 400 9pt/1.4 'Cambria', serif;
          color: var(--muted); letter-spacing: 0.8px; margin-top: 4px;
          text-transform: uppercase;
        }
        .doc-header .meta {
          text-align: right; font-size: 9pt; color: var(--muted);
          line-height: 1.45;
        }
        .doc-title { text-align: center; margin: 8px 0 28px; }
        .doc-title h1 {
          font: 700 15pt/1.25 'Cambria', serif;
          letter-spacing: 1.5px; text-transform: uppercase; margin: 0;
        }
        .doc-title .subtitle {
          font: 400 10pt/1.4 'Cambria', serif;
          letter-spacing: 0.6px; color: var(--muted);
          margin-top: 4px; text-transform: uppercase;
        }
        .section { margin-top: 18px; }
        .section h2 {
          font: 700 10.5pt/1 'Cambria', serif;
          letter-spacing: 1.6px; text-transform: uppercase;
          margin: 0 0 10px; padding: 0 0 4px;
          border-bottom: 1px solid var(--rule);
        }
        .data-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 2px 24px; margin: 8px 0 4px;
        }
        .data-grid .row { padding: 4px 0; border-bottom: 1px dotted #cbd5e1; }
        .data-grid .row .lbl {
          font: 600 9pt/1.4 'Cambria', serif;
          color: var(--muted); text-transform: uppercase;
          letter-spacing: 0.6px; display: block;
        }
        .data-grid .row .val { font-size: 11pt; color: var(--ink); }
        .data-grid .row.full { grid-column: 1 / -1; }
        .body-text p { text-align: justify; margin: 8px 0; }
        .body-text ul { margin: 8px 0 8px 22px; padding: 0; }
        .body-text ul li { margin: 4px 0; text-align: justify; }
        .signature-section { margin-top: 8px; page-break-inside: avoid; }
        .signature-section .place {
          margin: 16px 0 48px; text-align: center; font-size: 10.5pt;
        }
        .sig-row {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 64px; margin-top: 12px;
        }
        .sig-box { text-align: center; }
        .sig-line {
          border-top: 1px solid var(--ink); padding-top: 6px;
          font-size: 10pt; line-height: 1.4;
        }
        .sig-name {
          font: 700 10.5pt/1.2 'Cambria', serif;
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .sig-role {
          font-size: 9pt; color: var(--muted);
          letter-spacing: 0.5px; text-transform: uppercase; margin-top: 2px;
        }
        .sig-doc { font-size: 9pt; color: var(--muted); margin-top: 2px; }
        .witness-section { margin-top: 56px; page-break-inside: avoid; }
        .witness-section h4 {
          font: 700 9.5pt/1 'Cambria', serif;
          letter-spacing: 1.4px; text-transform: uppercase;
          margin: 0 0 24px; color: var(--muted);
        }
        .doc-footer {
          margin-top: 40px; padding-top: 12px;
          border-top: 1px solid var(--soft);
          font-size: 8.5pt; color: var(--muted); text-align: center;
          letter-spacing: 0.3px;
        }
        @media print {
          body { background: white; }
          .doc-wrap { box-shadow: none; margin: 0; padding: 0; max-width: none; }
          .doc-print-toolbar { display: none !important; }
          .signature-section, .witness-section { page-break-inside: avoid; }
        }
      `}</style>

      <div className="doc-print-toolbar">
        <button onClick={() => window.print()}>Imprimir / Salvar PDF</button>
        <button className="ghost" onClick={() => window.close()}>Fechar</button>
      </div>

      <div className="doc-wrap">
        <div className="doc-header">
          <div className="brand">
            MATELLI MOTORS
            <small>Compra e venda de veículos</small>
          </div>
          <div className="meta">
            CNPJ 62.352.611/0001-56<br />
            Ribeirão Preto/SP · CEP 14.021-612<br />
            {v.sale_date ? <>Emitido em {formatDateBR(v.sale_date)}<br /></> : ''}
            <div style={{
              marginTop: 8, padding: '4px 8px',
              border: '1px solid var(--rule)', borderRadius: 4,
              fontFamily: 'monospace', fontSize: '9pt', letterSpacing: '0.5px',
              display: 'inline-block', background: '#f8fafc'
            }}>
              TERMO Nº {String(v.id || '').slice(0, 8).toUpperCase()}
            </div>
          </div>
        </div>

        <div className="doc-title">
          <h1>Termo de entrega e recebimento de veículo</h1>
          <div className="subtitle">Instrumento particular</div>
        </div>

        <div className="body-text">
          <p>
            Pelo presente instrumento, a <strong>MATELLI MOTORS LTDA</strong>, inscrita no CNPJ sob
            nº <strong>62.352.611/0001-56</strong>, doravante denominada VENDEDORA, entrega ao
            COMPRADOR abaixo identificado o veículo descrito neste termo.
          </p>
        </div>

        <div className="section">
          <h2>I — Comprador</h2>
          <div className="data-grid">
            <div className="row"><span className="lbl">Nome / Razão social</span><span className="val">{v.buyer_name || '—'}</span></div>
            <div className="row"><span className="lbl">CPF / CNPJ</span><span className="val">{v.buyer_document ? formatDocBR(v.buyer_document) : '—'}</span></div>
            <div className="row"><span className="lbl">Telefone</span><span className="val">{v.buyer_phone ? formatPhoneBR(v.buyer_phone) : '—'}</span></div>
            <div className="row"><span className="lbl">E-mail</span><span className="val">{v.buyer_email || '—'}</span></div>
          </div>
        </div>

        <div className="section">
          <h2>II — Veículo entregue</h2>
          <div className="data-grid">
            <div className="row"><span className="lbl">Marca</span><span className="val">{v.brand || '—'}</span></div>
            <div className="row"><span className="lbl">Modelo</span><span className="val">{v.model || '—'}</span></div>
            <div className="row"><span className="lbl">Versão</span><span className="val">{v.version || '—'}</span></div>
            <div className="row"><span className="lbl">Cor</span><span className="val">{v.color || '—'}</span></div>
            <div className="row full"><span className="lbl">Ano de fabricação / modelo</span><span className="val">{(v.year_fabrication || '—')} / {(v.year || '—')}</span></div>
            <div className="row"><span className="lbl">Placa</span><span className="val">{v.plate || '—'}</span></div>
            <div className="row"><span className="lbl">Renavam</span><span className="val">{v.renavam || '—'}</span></div>
            <div className="row full"><span className="lbl">Chassi</span><span className="val">{v.chassis || '—'}</span></div>
            <div className="row full"><span className="lbl">Quilometragem no momento da entrega</span><span className="val">{v.km != null ? `${Number(v.km).toLocaleString('pt-BR')} km` : '—'}</span></div>
          </div>
        </div>

        <div className="section">
          <h2>III — Entrega</h2>
          <div className="data-grid">
            <div className="row"><span className="lbl">Data</span><span className="val">{v.sale_date ? formatDateBR(v.sale_date) : '—'}</span></div>
            <div className="row"><span className="lbl">Horário</span><span className="val">&nbsp;</span></div>
            <div className="row full"><span className="lbl">Local</span><span className="val">&nbsp;</span></div>
          </div>
        </div>

        <div className="body-text">
          <p>O COMPRADOR declara que recebeu o veículo acima identificado na data, horário e local indicados neste termo.</p>
          <p>A partir da entrega, o COMPRADOR assume integral responsabilidade pela posse, guarda, conservação, circulação e utilização do veículo, inclusive por:</p>
          <ul>
            <li>multas e infrações de trânsito;</li>
            <li>pedágios;</li>
            <li>tributos e encargos decorrentes de fatos posteriores à entrega;</li>
            <li>colisões, avarias e danos causados ou sofridos pelo veículo;</li>
            <li>furto, roubo ou perda;</li>
            <li>contratação e manutenção de seguro;</li>
            <li>danos causados a terceiros;</li>
            <li>utilização por pessoas por ele autorizadas;</li>
            <li>cumprimento das obrigações de transferência perante o órgão de trânsito.</li>
          </ul>
          <p>Caso qualquer multa, cobrança, tributo ou encargo decorrente de fato posterior à entrega seja lançado em nome da VENDEDORA ou do proprietário anterior, o COMPRADOR deverá providenciar sua regularização e reembolsar integralmente os valores pagos pela VENDEDORA.</p>
          <p>A retirada do veículo por pessoa autorizada pelo COMPRADOR produzirá os mesmos efeitos da entrega direta, permanecendo o COMPRADOR integralmente responsável pelo veículo.</p>
          <p>Este termo não afasta a garantia legal nem os direitos relacionados a eventual vício oculto preexistente à entrega, nos termos da legislação aplicável.</p>
          <p><strong>Data e horário de assinatura.</strong> A data e o horário registrados na assinatura deste termo correspondem à data e ao horário em que o veículo foi efetivamente retirado pelo COMPRADOR.</p>
          <p><strong>Vínculo com o contrato de compra e venda.</strong> O número deste termo é idêntico ao número do contrato de compra e venda celebrado entre as partes, ficando ambos os instrumentos expressamente vinculados e devendo ser interpretados em conjunto.</p>
          <p><strong>Assinatura eletrônica.</strong> A assinatura deste termo será realizada de forma eletrônica, com envio do link de assinatura pelo WhatsApp informado no cadastro do COMPRADOR, produzindo os mesmos efeitos da assinatura física.</p>
        </div>

        <div className="signature-section">
          <div className="place">
            Ribeirão Preto/SP, {v.sale_date ? formatDateBR(v.sale_date) : '_____ de __________________ de _______'}.
          </div>
          <br /><br /><br />
          <div className="sig-row">
            <div className="sig-box">
              <div className="sig-line">
                <div className="sig-name">Matelli Motors Ltda</div>
                <div className="sig-doc">CNPJ 62.352.611/0001-56</div>
                <div className="sig-role">Vendedora</div>
              </div>
            </div>
            <div className="sig-box">
              <div className="sig-line">
                <div className="sig-name">{v.buyer_name || '—'}</div>
                <div className="sig-doc">CPF/CNPJ {v.buyer_document ? formatDocBR(v.buyer_document) : '—'}</div>
                <div className="sig-role">Comprador</div>
              </div>
            </div>
          </div>
        </div>

        <div className="doc-footer">
          Matelli Motors Ltda · CNPJ 62.352.611/0001-56<br />
          Rua Triunfo, 1.349, sala 315 · Jardim Botânico · Ribeirão Preto/SP · CEP 14.021-612
        </div>
      </div>
    </>
  );
}
